// src/pages/QuestionsListPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import TopMenu from "../components/TopMenu";
import { useQuestions } from "../hooks/useQuestions";
import harnessBg from "../assets/images/harness-bg.png";
import { QuestionState } from "./types";
import { graphService } from "../services/EmailgraphService";

/** --- Reusable UI bits (match Year/Month pill look) --- */
type DivProps = React.HTMLAttributes<HTMLDivElement>;
const Pill: React.FC<DivProps> = ({ className = "", children, ...rest }) => (
  <div
    className={[
      "rounded-full px-5 py-4",
      "backdrop-blur-xl",
      "bg-gradient-to-r from-white/40 via-white/30 to-white/20",
      "ring-1 ring-white/40",
      "shadow-[inset_0_1px_2px_rgba(255,255,255,0.6),0_8px_20px_rgba(0,0,0,0.15)]",
      "hover:from-white/50 hover:via-white/40 hover:to-white/30 transition-colors",
      className,
    ].join(" ")}
    {...rest}
  >
    {children}
  </div>
);

const ActionButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: "blue" | "green" | "purple" | "neutral" }
> = ({ tone = "blue", className = "", children, ...props }) => {
  const tones: Record<string, string> = {
    blue: "bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-300",
    green: "bg-green-600 hover:bg-green-700 focus-visible:ring-green-300",
    purple: "bg-purple-600 hover:bg-purple-700 focus-visible:ring-purple-300",
    neutral: "bg-white/20 hover:bg-white/30 focus-visible:ring-white/40",
  };
  return (
    <button
      className={[
        "rounded-full px-4 py-2 text-white font-medium",
        "shadow-md transition-colors",
        "focus-visible:outline-none focus-visible:ring-2",
        tones[tone],
        className,
      ].join(" ")}
      {...props}
    >
      {children}
    </button>
  );
};


const QuestionsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { projectKey, phase, itemId } = useParams<{
    projectKey: string;
    phase: string;
    itemId: string;
  }>();

  // Redirect home if params missing
  useEffect(() => {
    if (!projectKey || !phase || !itemId) navigate("/");
  }, [projectKey, phase, itemId, navigate]);

  const { questions, loading, error } = useQuestions(projectKey!, itemId!);
  const [qs, setQs] = useState<QuestionState[]>([]);
  const [checkingMap, setCheckingMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setQs(questions);
  }, [questions]);

  const goBack = () => navigate(`/details/${projectKey}/${phase}/${itemId}`);
  const onSend = (q: QuestionState) =>
    navigate(`/send-email/${projectKey}/${phase}/${itemId}/${q.id}`);

  const onCheck = async (q: QuestionState) => {
    const fixedSubject = `${q.questionId} ${q.changeNumber} - `;
    setCheckingMap((m) => ({ ...m, [q.id]: true }));
    try {
      const updated = await graphService.pollInboxFast(q, fixedSubject);
      setQs((curr) => curr.map((x) => (x.id === q.id ? updated : x)));
      toast[updated.responseReceived ? "success" : "info"](
        updated.responseReceived ? "Reply detected!" : "No reply found yet"
      );
    } catch (err: any) {
      console.error(err);
      toast.error("Error checking inbox");
    } finally {
      setCheckingMap((m) => ({ ...m, [q.id]: false }));
    }
  };

  return (
    <div
      className="relative w-full min-h-screen bg-cover bg-center text-white"
      style={{ backgroundImage: `url(${harnessBg})` }}
    >
      <ToastContainer />
      <TopMenu />

      <div className="max-w-6xl mx-auto py-10 px-4 space-y-6">
        {/* Back + Header */}
        <div className="flex items-center justify-between gap-4">
          <ActionButton tone="neutral" onClick={goBack} aria-label="Go back to change details">
            ← Back
          </ActionButton>
        </div>

        {/* Content */}
        {loading ? (
          <div>Loading…</div>
        ) : error ? (
          <div className="text-red-300">{error}</div>
        ) : (
          <div className="space-y-4">
            {qs.map((q) => (
              <Pill key={q.id} className="group">
                <div className="flex items-center gap-4">
                  {/* Question text */}
                  <div className="flex-1 min-w-0">
                  <p
className="font-medium text-lg sm:text-xl lg:text-2xl leading-snug drop-shadow-md whitespace-normal break-words"
   title={q.description}
 >
   {q.description}
</p>
                    {/* Meta line – bigger, clearer */}
                    {q.lastSent && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-base sm:text-lg">
                        <div className="truncate">
                          <span className="text-white/80 font-medium">Last Sent:</span>{" "}
                          <span className="font-semibold">
                            {new Date(q.lastSent).toLocaleString()}
                          </span>
                        </div>

                        <div className="truncate">
                          <span className="text-white/80 font-medium">Response:</span>{" "}
                          <span
                            className={[
                              "inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold",
                              q.responseReceived
                                ? "bg-green-500/20 text-green-200 ring-1 ring-green-400/40"
                                : "bg-yellow-500/20 text-yellow-200 ring-1 ring-yellow-400/40",
                            ].join(" ")}
                          >
                            {q.responseReceived ? "Yes" : "No"}
                          </span>
                        </div>

                        <div>
                          <ActionButton
                            tone="green"
                            onClick={() => onCheck(q)}
                            disabled={!!checkingMap[q.id]}
                            aria-label={`Check email reply for question ${q.id}`}
                            className="w-full sm:w-auto"
                          >
                            {checkingMap[q.id] ? "Checking…" : "Check Response"}
                          </ActionButton>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Send button */}
                  <ActionButton
                    tone="blue"
                    onClick={() => onSend(q)}
                    aria-label={`Send email for question ${q.id}`}
                    className="shrink-0"
                  >
                    Send
                  </ActionButton>
                </div>
              </Pill>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionsListPage;
